// /sourcing/history — 反馈历史子页
// v1.12 P0: 从主路由抽出来, 主路由只显示输入+结果, 历史独立页
// 数据: sourcingApi.listFeedback(20) — 跟原主路由共用
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { EmptyState } from '../components/EmptyState';
import { Icon } from '../components/Icon';
import { Skeleton } from '../components/Skeleton';
import { Toast } from '../components/Toast';
import { Card } from '../components/ui/Card';
import { sourcingApi } from '../lib/api';

export const Route = createFileRoute('/sourcing/history')({
  component: SourcingHistoryPage,
});

function SourcingHistoryPage() {
  const queryClient = useQueryClient();
  const [toast, setToast] = useState<{ kind: 'success' | 'error'; msg: string } | null>(null);

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['sourcing-feedback'],
    queryFn: () => sourcingApi.listFeedback(20),
    staleTime: 30_000,
  });

  function openGoogle(query: string) {
    window.open(`https://www.google.com/search?q=${encodeURIComponent(query)}`, '_blank');
  }

  return (
    <div className="animate-fade-up">
      {/* 顶部返回条 */}
      <div className="mb-4 flex items-center gap-2 text-sm">
        <Link
          to="/sourcing"
          className="inline-flex items-center gap-1 text-ink-3 hover:text-accent min-h-[44px] px-2 -ml-2"
          aria-label="返回找客中心"
        >
          <Icon name="arrow-left" size="sm" aria-hidden />
          返回找客中心
        </Link>
      </div>

      <header className="mb-5">
        <h1 className="text-xl sm:text-2xl font-semibold text-ink leading-tight flex items-center gap-2">
          <Icon name="thumbs-up" size="md" className="text-accent" aria-hidden />
          反馈历史
        </h1>
        <p className="text-xs sm:text-sm text-ink-3 mt-1 leading-relaxed">
          你给每条 dork 的 👍/👎 评价, AI 用这些学你偏好。
        </p>
      </header>

      {/* AI 偏好摘要 */}
      {data?.bias &&
        (data.bias.preferredTemplateIds.length > 0 || data.bias.avoidedPatterns.length > 0) && (
          <Card padding="md" className="mb-4 border-accent/30 bg-accent-soft/40">
            <h2 className="text-sm font-semibold text-ink-2 mb-2 flex items-center gap-1.5">
              <Icon name="sparkles" size="sm" className="text-accent" aria-hidden />
              AI 已学到的偏好
            </h2>
            <div className="space-y-1.5 text-xs">
              {data.bias.preferredTemplateIds.length > 0 && (
                <div>
                  <span className="text-ink-2">优先选:</span>{' '}
                  <span className="text-good font-medium">
                    {data.bias.preferredTemplateIds.join(', ')}
                  </span>
                </div>
              )}
              {data.bias.avoidedPatterns.length > 0 && (
                <div>
                  <span className="text-ink-2">会避免:</span>{' '}
                  <span className="text-bad font-medium">
                    {data.bias.avoidedPatterns.join(', ')}
                  </span>
                </div>
              )}
            </div>
          </Card>
        )}

      {/* 加载 / 错误 / 空 / 列表 */}
      {isLoading && (
        <div className="space-y-2">
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
        </div>
      )}

      {isError && (
        <EmptyState
          icon="alert-triangle"
          title="加载反馈失败"
          description={(error as Error).message}
          action={
            <button
              type="button"
              onClick={() => refetch()}
              className="px-3 py-2 bg-accent text-white rounded text-sm hover:bg-accent-2"
            >
              重试
            </button>
          }
        />
      )}

      {!isLoading && !isError && data?.feedback.length === 0 && (
        <EmptyState
          icon="thumbs-up"
          title="还没有反馈"
          description="在找客中心给 dork 点 赞/踩, AI 才能学你偏好"
          action={
            <Link
              to="/sourcing"
              className="px-3 py-2 bg-accent text-white rounded text-sm hover:bg-accent-2 inline-flex items-center gap-1"
            >
              <Icon name="sparkles" size="sm" aria-hidden />
              去找 dork
            </Link>
          }
        />
      )}

      {data?.feedback && data.feedback.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs text-ink-3 mb-2">
            共 <b>{data.total}</b> 条反馈, 显示最新 {data.feedback.length} 条
          </p>
          {data.feedback.map((row) => (
            <Card key={row.id} padding="sm" className="hover:border-accent/40 transition-colors">
              <div className="flex items-start gap-2.5">
                <span
                  className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                    row.rating === 'useful' ? 'bg-good/15 text-good' : 'bg-bad/15 text-bad'
                  }`}
                  aria-label={row.rating === 'useful' ? '赞' : '踩'}
                >
                  <Icon
                    name={row.rating === 'useful' ? 'thumbs-up' : 'thumbs-down'}
                    size="sm"
                    aria-hidden
                  />
                </span>
                <div className="flex-1 min-w-0">
                  <button
                    type="button"
                    onClick={() => openGoogle(row.dorkQuery)}
                    className="text-sm text-ink-2 hover:text-accent break-all text-left w-full leading-snug"
                  >
                    {row.dorkQuery}
                  </button>
                  <div className="text-[10px] sm:text-xs text-ink-3 mt-1 flex flex-wrap gap-x-2 gap-y-0.5">
                    {row.dorkTemplateId && (
                      <span>
                        模板:{' '}
                        <code className="bg-surface-2 px-1 rounded font-mono">
                          {row.dorkTemplateId}
                        </code>
                      </span>
                    )}
                    <span>{new Date(row.createdAt).toLocaleString('zh-CN')}</span>
                  </div>
                </div>
                <Icon name="external-link" size="sm" className="shrink-0 text-ink-3" aria-hidden />
              </div>
            </Card>
          ))}

          {/* 刷新按钮 */}
          <div className="pt-2 text-center">
            <button
              type="button"
              onClick={() => {
                queryClient.invalidateQueries({ queryKey: ['sourcing-feedback'] });
                setToast({ kind: 'success', msg: '已刷新' });
              }}
              className="text-xs text-ink-3 hover:text-accent inline-flex items-center gap-1 min-h-[36px] px-3"
            >
              <Icon name="refresh-cw" size="xs" aria-hidden />
              刷新
            </button>
          </div>
        </div>
      )}

      {toast && <Toast kind={toast.kind} message={toast.msg} onClose={() => setToast(null)} />}
    </div>
  );
}
